/**
 * ParticipantsStep — Create Test Wizard
 *
 * In-app editable grid (ParticipantsGrid) replaces the old Excel
 * upload/download flow. Same downstream contract as before: onNext
 * receives { participants } in the same shape the xlsx path used to
 * produce, so everything after this step (wizardData merge, the final
 * POST /tests call) is unchanged.
 */
import React, { useEffect, useMemo, useState } from "react";
import { apiClient, ServiceError } from "../../services/apiClient";
import { showToast } from "../../lib/toast";
import { Button } from "../../components/ui/button";
import { XlsxErrorList, type XlsxRowError } from "../../components/XlsxErrorList";
import {
  ParticipantsGrid,
  createDefaultRows,
  validateRow,
  type ParticipantRow,
} from "../../components/ParticipantsGrid";
import type { ComboboxOption } from "../../components/Combobox";
import type { WizardStepProps } from "./VersionSelectionStep";
import { Info } from "lucide-react";

interface ValidateRowsResponse {
  participants: Array<{ name: string; email: string; linkedin_url: string; timezone: string }>;
  errors: XlsxRowError[];
}

function isNonEmptyRow(row: ParticipantRow): boolean {
  return Boolean(row.name.trim() || row.email.trim() || row.linkedin_url.trim() || row.timezone.trim());
}

export function ParticipantsStep({ wizardData, onNext, onBack }: WizardStepProps) {
  const isBaseline = Boolean(wizardData?.is_baseline_source);

  const [rows, setRows] = useState<ParticipantRow[]>(() => {
    const saved = wizardData?.participants;
    if (Array.isArray(saved) && saved.length > 0) {
      return saved.map((p: Record<string, unknown>, i: number) => ({
        id: (p.id as string) ?? `saved-${i}`,
        name: (p.name as string) ?? "",
        email: (p.email as string) ?? "",
        linkedin_url: (p.linkedin_url as string) ?? "",
        timezone: (p.timezone as string) ?? "",
      }));
    }
    return createDefaultRows(isBaseline ? 1 : undefined);
  });

  const [timezoneOptions, setTimezoneOptions] = useState<ComboboxOption[]>([]);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<XlsxRowError[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<string[]>("/api/timezones")
      .then((zones) => {
        if (!cancelled) setTimezoneOptions(zones.map((z) => ({ value: z, label: z })));
      })
      .catch(() => {
        if (!cancelled) {
          setTimezoneOptions([]);
          showToast({
            variant: "error",
            title: "Couldn't load timezones",
            description: "Timezone options failed to load. Refresh and try again.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const validTimezones = useMemo(() => new Set(timezoneOptions.map((o) => o.value)), [timezoneOptions]);

  const fieldErrors = useMemo(() => {
    const result: Record<string, string> = {};
    for (const row of rows) {
      if (!isNonEmptyRow(row)) continue;
      const errors = validateRow(row, validTimezones);
      for (const [field, message] of Object.entries(errors)) {
        const key = `${row.id}.${field}`;
        if (touched.has(key)) result[key] = message as string;
      }
    }
    return result;
  }, [rows, validTimezones, touched]);

  const handleFieldBlur = (rowId: string, field: string) => {
    setTouched((prev) => new Set(prev).add(`${rowId}.${field}`));
  };

  const handleNext = async () => {
    setSubmitError(null);
    setRowErrors([]);

    const filled = rows.filter(isNonEmptyRow);
    if (filled.length === 0) {
      setSubmitError("Add at least one participant before continuing.");
      return;
    }

    const blockingErrors = filled.some((row) => Object.keys(validateRow(row, validTimezones)).length > 0);
    if (blockingErrors) {
      setTouched(new Set(filled.flatMap((row) => (["name", "email", "linkedin_url", "timezone"] as const).map((f) => `${row.id}.${f}`))));
      setSubmitError("Fix the highlighted fields before continuing.");
      return;
    }

    const payload = filled.map((row) => ({
      name: row.name.trim(),
      email: row.email.trim(),
      linkedin_url: row.linkedin_url.trim(),
      timezone: row.timezone.trim(),
    }));

    setIsSubmitting(true);
    try {
      const result = await apiClient.post<ValidateRowsResponse>(
        "/api/tests/participants/validate-rows",
        { participants: payload },
      );
      onNext?.({ participants: result.participants });
    } catch (err) {
      if (err instanceof ServiceError && err.details && Array.isArray(err.details.errors)) {
        const errors = err.details.errors as XlsxRowError[];
        setRowErrors(errors);
        showToast({
          variant: "error",
          title: "Import rejected",
          description: `${errors.length} row issue${errors.length === 1 ? "" : "s"}. See details below.`,
        });
      } else {
        const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
        setSubmitError(message);
        showToast({ variant: "error", title: "Validation failed", description: message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Add Participants</h2>
      <p className="text-sm text-brand-steel mb-6">
        Add one row per participant — Name, Email, LinkedIn URL, and Timezone are all required. Blank rows are ignored.
      </p>

      {isBaseline && (
        <div className="flex items-start gap-2 p-4 mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-800">
          <Info size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm">
            Baseline source tests are limited to <strong>one candidate</strong>.
          </p>
        </div>
      )}

      <ParticipantsGrid
        rows={rows}
        onChange={setRows}
        isBaseline={isBaseline}
        timezoneOptions={timezoneOptions}
        fieldErrors={fieldErrors}
        onFieldBlur={handleFieldBlur}
      />

      {submitError && (
        <div role="alert" className="mb-2 text-xs text-brand-error">
          {submitError}
        </div>
      )}
      {rowErrors.length > 0 && (
        <div className="mb-4">
          <XlsxErrorList errors={rowErrors} />
        </div>
      )}

      <div className="flex justify-between mt-4">
        <div>
          {onBack && (
            <Button type="button" variant="outline" onClick={() => onBack?.()} className="min-w-25 border-brand-steel/40">
              Back
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={handleNext}
          disabled={isSubmitting}
          className="min-w-25 bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-6 py-2"
        >
          {isSubmitting ? "Validating..." : "Next"}
        </Button>
      </div>
    </div>
  );
}

export default ParticipantsStep;
